import Icon, { IconType } from 'components/Icon';
import Logo from 'components/Widget/Logo';
import ProjectListItem from 'components/Sidebar/ListItem/ProjectListItem';
import { trackDesignSystemAction, trackSceneAction } from 'analytics';
import forge from 'apis/forge';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
	setGuestSignupModalVisible,
	setSettingsModalVisible,
	setSidebarVisible,
	setSidebarHovered,
} from 'redux/mode/actions';
import { RootReducer, selectIsLoadingAnything } from 'redux/reducers';
import { useNavigate } from 'react-router';
import { useTheme } from 'styled-components';
import {
	selectDesignSystems,
	selectGuestScenes,
	selectIsGuest,
	selectScenes,
	selectUser,
	selectUserSettings,
} from 'redux/db/reducers';
import { selectIsMobile } from 'redux/mode/reducers';
import { DesignSystem, Scene } from 'dragonmouse-shared/interfaces';
import * as routes from 'routes';
import * as S from './styles';
import ProfileIcon from 'components/Panel/RightPanel/ProfileIcon';
import { useSidebarWidth } from 'hooks/useSidebarWidth';
import { Loader } from 'components/Widget/Loader';
import DesignSystemListItem from './ListItem/DesignSystemListItem';
import NewButton from './NewButton';
import { useState, useEffect } from 'react';

const Sidebar = () => {
	const dispatch = useDispatch();
	const theme = useTheme();
	const navigate = useNavigate();
	const sidebarWidth = useSidebarWidth();

	const {
		sidebarVisible: sidebarVisibleFromState,
		sidebarDocked,
		sidebarHovered,
		contextMenu,
		scenes,
		guestScenes,
		designSystems,
		user,
		isMobile,
		activeSceneId,
		activeDesignSystemId,
		loading,
		designSystemsEnabled,
		isGuest,
	} = useSelector((r: RootReducer) => {
		return {
			sidebarVisible: r.mode.sidebarVisible,
			sidebarDocked: r.mode.sidebarDocked,
			sidebarHovered: r.mode.sidebarHovered,
			contextMenu: r.mode.contextMenu,
			scenes: selectScenes(r.db),
			guestScenes: selectGuestScenes(r.db),
			designSystems: selectDesignSystems(r.db),
			user: selectUser(r.db),
			isMobile: selectIsMobile(r.mode),
			activeSceneId: r.mode.activeSceneId,
			activeDesignSystemId: r.mode.activeDesignSystemId,
			loading: selectIsLoadingAnything(r),
			designSystemsEnabled: selectUserSettings(r.db).designSystemsEnabled,
			isGuest: selectIsGuest(r.db),
		};
	}, shallowEqual);

	// Compute sidebar visibility: Mobile uses sidebarVisible directly, Desktop uses docked || hovered
	const sidebarVisible = isMobile ? sidebarVisibleFromState : sidebarDocked || sidebarHovered;

	//----------------------------------------------------------------------------------------------
	// STATE MANAGEMENT
	//----------------------------------------------------------------------------------------------
	const [showAllDesignSystems, setShowAllDesignSystems] = useState(false);
	const [showAllProjects, setShowAllProjects] = useState(false);
	const [showAllGuestProjects, setShowAllGuestProjects] = useState(false);

	// Reset expanded state when user selects an item
	useEffect(() => {
		setShowAllDesignSystems(false);
		setShowAllProjects(false);
		setShowAllGuestProjects(false);
	}, [activeSceneId, activeDesignSystemId]);

	//----------------------------------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------------------------------

	const onLoadScene = (scene: Scene, newTab?: boolean) => {
		if (newTab) {
			trackSceneAction('open_in_new_tab');
			window.open(routes.scene(scene.id, scene.title), '_blank', 'noopener,noreferrer');
		} else {
			trackSceneAction('open');
			navigate(routes.scene(scene.id, scene.title));
			if (isMobile) {
				dispatch(setSidebarVisible(false));
			}
		}
	};

	const onLoadDesignSystem = (designSystem: DesignSystem, newTab?: boolean) => {
		if (newTab) {
			trackDesignSystemAction('open_in_new_tab');
			window.open(
				routes.designSystem(designSystem.id, designSystem.title),
				'_blank',
				'noopener,noreferrer'
			);
		} else {
			trackDesignSystemAction('open');
			navigate(routes.designSystem(designSystem.id, designSystem.title));
		}
	};

	const onDuplicateProject = async (sceneId: string) => {
		if (isGuest) {
			dispatch(setGuestSignupModalVisible(true));
			return;
		}
		try {
			await forge.duplicateScene.withToast()({ sceneId });
		} catch (e) {}
	};

	const openChromePlugin = () => {
		window.open(
			'https://chromewebstore.google.com/detail/moonchild/gemkdajnghnanjoehknfbacbjnndfdph',
			'_blank',
			'noopener,noreferrer'
		);
	};

	const handleMouseEnter = () => {
		if (!isMobile && !sidebarDocked) {
			dispatch(setSidebarHovered(true));
		}
	};

	const handleMouseLeave = () => {
		// Don't close sidebar if context menu is open (user is right-clicking)
		//
		// This is part of our compromise solution to handle context menu interactions in the sidebar.
		// The fundamental challenge is that, when the mouse enters the context menu, it triggers a
		// mouseLeave event on the sidebar.
		//
		// TLDR we can't detect things cleanly without a major refactor of context menu.
		// So accepting compromise:
		//   - Block closing while a context menu is open.
		//   - When the menu closes (accept or cancel), the sidebar stays open until the user hovers away
		//   - Sometimes this means the user has to hover onto, and then off of the sidebar to close it.
		//
		// This is not ideal but, this is not a time to let perfect be the enemy of good.
		// This is not a frequent interaction after all...
		//
		// ALTERNATIVES CONSIDERED:
		// 1. Close sidebar immediately when menu closes - feels abrupt, especially when making a selection
		// 2. Detect hover state when menu closes (via refs, timers, :hover) - unreliable, complex
		// 3. Click-outside-to-close model - more predictable but changes the interaction paradigm
		//
		// TRADEOFF:
		// The sidebar may linger open after menu interaction until user hovers away.
		// This is acceptable because it's predictable and the code stays simple.
		if (!isMobile && !sidebarDocked && !contextMenu) {
			dispatch(setSidebarHovered(false));
		}
	};

	//----------------------------------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------------------------------
	const DESIGN_SYSTEMS_LIMIT = 5;
	const PROJECTS_LIMIT = 15;

	const sortedScenes: Scene[] = [...scenes]
		.sort((a, b) => {
			const getMostRecentAction = (scene: Scene) => {
				const creationDate = new Date(scene.createdAt);
				if (!scene.metadata?.openActions || !user?.id) return creationDate;
				const userAction = scene.metadata.openActions[user.id];
				if (!userAction) return creationDate;
				return new Date(userAction);
			};
			return getMostRecentAction(b).getTime() - getMostRecentAction(a).getTime();
		})
		.filter((s: Scene) => !s.viewOnly);

	const sortedGuestScenes: Scene[] = [...guestScenes].sort((a, b) => {
		const getMostRecentAction = (scene: Scene) => {
			const creationDate = new Date(scene.createdAt);
			if (!scene.metadata?.openActions || !user?.id) return creationDate;
			const userAction = scene.metadata.openActions[user.id];
			if (!userAction) return creationDate;
			return new Date(userAction);
		};
		return getMostRecentAction(b).getTime() - getMostRecentAction(a).getTime();
	});

	const sortedDesignSystems: DesignSystem[] = [...designSystems].sort((a, b) => {
		const getMostRecentAction = (designSystem: DesignSystem) => {
			const creationDate = new Date(designSystem.createdAt);
			if (!designSystem.metadata?.openActions || !user?.id) return creationDate;
			const userAction = designSystem.metadata.openActions[user.id];
			if (!userAction) return creationDate;
			return new Date(userAction);
		};
		return getMostRecentAction(b).getTime() - getMostRecentAction(a).getTime();
	});

	// Create displayed arrays based on expanded state
	const displayedDesignSystems = showAllDesignSystems
		? sortedDesignSystems
		: sortedDesignSystems.slice(0, DESIGN_SYSTEMS_LIMIT);

	const displayedScenes = showAllProjects ? sortedScenes : sortedScenes.slice(0, PROJECTS_LIMIT);

	const displayedGuestScenes = showAllGuestProjects
		? sortedGuestScenes
		: sortedGuestScenes.slice(0, PROJECTS_LIMIT);

	// Calculate remaining counts for "View all" text
	const remainingDesignSystems = sortedDesignSystems.length - displayedDesignSystems.length;
	const remainingProjects = sortedScenes.length - displayedScenes.length;
	const remainingGuestProjects = sortedGuestScenes.length - displayedGuestScenes.length;

	return (
		<>
			{isMobile && sidebarVisible && (
				<S.Overlay onClick={() => dispatch(setSidebarVisible(false))} />
			)}
			<S.Base
				isMobile={isMobile}
				sidebarVisible={sidebarVisible}
				sidebarDocked={sidebarDocked}
				sidebarWidth={sidebarWidth}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
			>
				{isMobile ? (
					<S.MobileHeader>
						<S.MobileHeaderContent>
							<S.LogoIconWrapper>
								<Icon type={IconType.MOONCHILD} width={24} height={24} color={theme.gray[1000]} />
							</S.LogoIconWrapper>
							<S.Moonchild>
								<Logo height={18} color={theme.gray[1000]} />
							</S.Moonchild>
						</S.MobileHeaderContent>
						<S.CloseButton onClick={() => dispatch(setSidebarVisible(false))}>
							<Icon type={IconType.CLOSE} width={20} height={20} color={theme.gray[1000]} />
						</S.CloseButton>
					</S.MobileHeader>
				) : (
					<S.DesktopHeader>
						<Logo height={15} color={theme.gray[800]} />
						<S.DesktopHeaderButton>
							<S.ProfileButton
								onClick={() => {
									navigate(routes.profile());
								}}
							>
								<ProfileIcon size={24} />
							</S.ProfileButton>
							<S.SettingsButton onClick={() => dispatch(setSettingsModalVisible(true))}>
								<Icon type={IconType.SETTINGS} width={20} height={20} color={theme.gray[400]} />
							</S.SettingsButton>
						</S.DesktopHeaderButton>
					</S.DesktopHeader>
				)}
				<NewButton />
				<S.Content>
					{loading ? (
						<S.LoadingWrapper>
							<Loader width={24} height={24} />
						</S.LoadingWrapper>
					) : (
						<>
							{designSystemsEnabled && !isMobile && sortedDesignSystems.length > 0 && (
								<>
									<S.SectionHeader>My Design Systems</S.SectionHeader>
									<S.ProjectsList>
										{displayedDesignSystems.map((designSystem: DesignSystem) => (
											<DesignSystemListItem
												key={designSystem.id}
												designSystem={designSystem}
												isActive={designSystem.id === activeDesignSystemId}
												onClick={() => onLoadDesignSystem(designSystem, false)}
												onCommandClick={() => onLoadDesignSystem(designSystem, true)}
											/>
										))}
										{remainingDesignSystems > 0 && (
											<S.ViewAllLink onClick={() => setShowAllDesignSystems(true)}>
												{remainingDesignSystems} more
											</S.ViewAllLink>
										)}
									</S.ProjectsList>
								</>
							)}
							{sortedGuestScenes.length > 0 && (
								<>
									<S.SectionHeader>Shared Projects</S.SectionHeader>
									<S.ProjectsList>
										{displayedGuestScenes.map((scene: Scene) => (
											<ProjectListItem
												key={scene.id}
												scene={scene}
												isActive={scene.id === activeSceneId}
												onClick={() => onLoadScene(scene, false)}
												onCommandClick={() => onLoadScene(scene, true)}
												onDuplicateProject={onDuplicateProject}
											/>
										))}
										{remainingGuestProjects > 0 && (
											<S.ViewAllLink onClick={() => setShowAllGuestProjects(true)}>
												{remainingGuestProjects} more
											</S.ViewAllLink>
										)}
									</S.ProjectsList>
								</>
							)}
							{sortedScenes.length > 0 && (
								<>
									<S.SectionHeader>My Projects</S.SectionHeader>
									<S.ProjectsList>
										{displayedScenes.map((scene: Scene) => (
											<ProjectListItem
												key={scene.id}
												scene={scene}
												isActive={scene.id === activeSceneId}
												onClick={() => onLoadScene(scene, false)}
												onCommandClick={() => onLoadScene(scene, true)}
												onDuplicateProject={onDuplicateProject}
											/>
										))}
										{remainingProjects > 0 && (
											<S.ViewAllLink onClick={() => setShowAllProjects(true)}>
												{remainingProjects} more
											</S.ViewAllLink>
										)}
									</S.ProjectsList>
								</>
							)}
						</>
					)}
				</S.Content>
				{!isGuest && (
					<S.Footer>
						{!isMobile && (
							<S.ChromePluginButton onClick={openChromePlugin}>
								Chrome Plugin
								<Icon
									type={IconType.OPEN_EXTERNAL}
									width={16}
									height={16}
									color={theme.gray[1000]}
								/>
							</S.ChromePluginButton>
						)}
						{isMobile && (
							<S.ProfileSettingsRow>
								<S.ProfileButton
									onClick={() => {
										navigate(routes.profile());
										if (isMobile) {
											dispatch(setSidebarVisible(false));
										}
									}}
								>
									<ProfileIcon size={32} />
									<S.ProfileName>{user?.displayName || ''}</S.ProfileName>
								</S.ProfileButton>
							</S.ProfileSettingsRow>
						)}
					</S.Footer>
				)}
			</S.Base>
		</>
	);
};

export default Sidebar;
